import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import { useWebSocket } from "../../../context/WebSocketContext";
import { GroupProps, profileType } from "../GroupItem/GroupItem";
import LeftArrow from "../../../assets/Left 1.svg";
import SendMessageIcon from "../../../assets/sendmessageicon.svg";
// import AttachImageIcon from "../../../assets/Group 58.svg";
// import { uploadImage } from "../../../services/NetworkServices";
import "./GroupChatDetails.css";
import { Box, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AgoraCallModal from '../../../components/AgoraCall/AgoraCallModal';
import { getAgoraToken } from '../../../services/NetworkServices';

type Props = {
  groupInfo: GroupProps | null;
  onClick: () => void;
  onClickDetails: () => void;
  goBack: () => void;
};

type chatProps = {
  createdAt: string;
  message: string;
  senderFirstName: string;
  senderId: string;
  senderLastName: string;
  senderProfilePic: string;
  type?: "text" | "call_event";
  metadata?: {
    callEvent?: string;
    durationSeconds?: number;
  };
  _id: string;
};

type CallMode = 'audio' | 'video';
type IncomingCall = {
  callerUserId: string;
  channelName: string | null;
  callType?: CallMode;
};

type PreparedAgoraCall = {
  token: string;
  appId: string;
  channelName: string;
  uid: number;
};

interface GroupedMessages {
  date: string;
  messages: chatProps[];
}

type userProps = {
  user: {
    _id: string;
    userId?: {
      _id?: string;
    };
  };
};

const GroupChatDetails: React.FC<Props> = ({
  groupInfo,
  onClickDetails,
  goBack,
}) => {
  const { socket } = useWebSocket();

  const listRef = useRef<any>(null);
  const inputRef = useRef<any>(null);

  const [inputValue, setInputValue] = useState("");
  const [chatHistory, setChatHistoryData] = useState<GroupedMessages[]>([]);
  const [chatData, setChatData] = useState<chatProps[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const [callMode, setCallMode] = useState<CallMode>('video');
  const [activeChannelName, setActiveChannelName] = useState('');
  const [activePeerUserId, setActivePeerUserId] = useState('');
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [isDialing, setIsDialing] = useState(false);
  const [callStatusText, setCallStatusText] = useState('');
  const [avatarErrors, setAvatarErrors] = useState<Record<string, boolean>>({});
  const [groupCallStartedAt, setGroupCallStartedAt] = useState<number | null>(null);
  const [preparedCall, setPreparedCall] = useState<PreparedAgoraCall | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const jwtToken: string = localStorage.getItem("token") ?? "";
  const user = jwtDecode<userProps>(jwtToken);
  const appId = process.env.REACT_APP_AGORA_APP_ID;
  const debugAgora = process.env.NODE_ENV !== 'production';
  const currentUserId = user?.user?._id || user?.user?.userId?._id || '';

  const getProfileUserId = (profile: profileType) => {
    if (profile.userId && typeof profile.userId === 'object' && '_id' in profile.userId) {
      return profile.userId._id || '';
    }
    return profile._id || '';
  };

  const targetUserId = (groupInfo?.profileIds || [])
    .map(getProfileUserId)
    .find((id) => id && id !== currentUserId) || '';
  const participantUserIds = Array.from(
    new Set(
      (groupInfo?.profileIds || [])
        .map(getProfileUserId)
        .filter((id) => !!id && id !== currentUserId)
    )
  );
  const groupChannelPrefix = groupInfo?._id ? `group_${groupInfo._id}_` : '';
  const chatParticipantIds = useMemo(
    () => new Set([currentUserId, ...participantUserIds].filter(Boolean)),
    [currentUserId, participantUserIds]
  );

  const shouldShowFallbackAvatar = (key: string, src?: string) =>
    !src || avatarErrors[key];
  const isDirectChat =
    (groupInfo?.groupName || '').toLowerCase() === 'direct' ||
    (groupInfo?.profileIds?.length || 0) === 2;

  const calleeProfile = (groupInfo?.profileIds || []).find((p) => {
    const pid = getProfileUserId(p);
    return pid && pid !== currentUserId;
  });
  const calleeName = isDirectChat && calleeProfile
    ? [calleeProfile.firstName, calleeProfile.lastName].filter(Boolean).join(' ')
    : groupInfo?.groupName || '';
  const calleeProfilePic = isDirectChat && calleeProfile
    ? calleeProfile.profilePic
    : '';

  const buildChannelName = () => {
    const idPart = `${currentUserId || 'u'}_${targetUserId || 'u'}_${Date.now()}`;
    return `channel_${idPart.replace(/[^a-zA-Z0-9_]/g, '')}`.slice(0, 64);
  };
  const buildAgoraUid = () => {
    const source = `${currentUserId || 'u'}_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
    let hash = 0;
    for (let i = 0; i < source.length; i += 1) {
      hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
    }
    const normalized = hash % 2147483647;
    return normalized === 0 ? 1 : normalized;
  };
  const prepareAgoraCall = useCallback(
    async (requestedChannelName: string, requestedUid: number) => {
      if (debugAgora) {
        // eslint-disable-next-line no-console
        console.log('[AgoraCall] request token', {
          requestedChannelName,
          requestedUid,
          currentUserId,
        });
      }
      const response = await getAgoraToken({
        channelName: requestedChannelName,
        uid: requestedUid,
      });
      if (!response.data?.status || !response.data?.data) {
        throw new Error(response.data?.message || 'Failed to get Agora token.');
      }
      const tokenData = response.data.data;
      if (debugAgora) {
        // eslint-disable-next-line no-console
        console.log('[AgoraCall] token prepared', {
          requestedChannelName,
          responseChannelName: tokenData.channelName,
          responseUid: tokenData.uid,
          responseAppId: tokenData.appId,
          expiresAt: tokenData.expiresAt,
        });
      }
      return {
        token: tokenData.token,
        appId: tokenData.appId || appId || '',
        channelName: requestedChannelName || tokenData.channelName,
        uid: tokenData.uid && tokenData.uid > 0 ? tokenData.uid : requestedUid,
      };
    },
    [appId, currentUserId, debugAgora]
  );
  const belongsToCurrentDirectChat = useCallback(
    (userId?: string | null) => isDirectChat && !!userId && chatParticipantIds.has(userId),
    [chatParticipantIds, isDirectChat]
  );
  const belongsToCurrentGroupChat = useCallback(
    (channel?: string | null) =>
      !isDirectChat && !!groupChannelPrefix && !!channel && channel.startsWith(groupChannelPrefix),
    [groupChannelPrefix, isDirectChat]
  );

  const groupMessagesByDate = (messages: chatProps[]): GroupedMessages[] => {
    const grouped: { [key: string]: chatProps[] } = {};

    messages.forEach((message) => {
      const date = new Date(message.createdAt);
      const today = new Date();
      let dateKey = "";

      if (date.toDateString() === today.toDateString()) {
        dateKey = "Today";
      } else if (
        date.toDateString() ===
        new Date(today.setDate(today.getDate() - 1)).toDateString()
      ) {
        dateKey = "Yesterday";
      } else {
        dateKey = moment(date).format("DD/MM/YYYY"); // Format as needed
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(message);
    });

    return Object.entries(grouped).map(([date, messages]) => ({
      date,
      messages,
    }));
  };

  const hydrateMessages = useCallback((rawMessages: chatProps[]) => {
    const sorted = [...rawMessages].sort(
      (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
    );
    setChatData(sorted);
    setChatHistoryData(groupMessagesByDate(sorted));
  }, []);

  useEffect(() => {
    socket &&
      socket.emit("get-chat-history", {
        roomId: groupInfo && groupInfo._id,
      });
  }, [socket, groupInfo]);

  useEffect(() => {
    if (!socket) return;
    const onChatHistory = (data: chatProps[]) => {
      hydrateMessages(data || []);
    };
    socket.on("chat-history", onChatHistory);

    return () => {
      socket.off("chat-history", onChatHistory);
    };
  }, [socket, hydrateMessages]);

  useEffect(() => {
    if (!socket) return;
    const onReceiveMessage = (data: chatProps[] | chatProps) => {
      const newMessages = Array.isArray(data) ? data : [data];
      hydrateMessages([...chatData, ...newMessages]);
    };
    socket.on("receive-message", onReceiveMessage);
    return () => {
      socket.off("receive-message", onReceiveMessage);
    };
  }, [socket, chatData, hydrateMessages]);

  const sendMessageWithAck = async (msg: string) => {
    socket &&
      socket.emit("send-message", {
        roomId: groupInfo && groupInfo._id,
        message: msg,
      });
  };

  const sendGroupCallEvent = (
    callEvent: 'initiated' | 'ended' | 'rejected' | 'cancelled',
    durationSeconds?: number
  ) => {
    if (!socket || !groupInfo?._id) return;
    const eventMessage =
      callEvent === 'initiated'
        ? 'Call initiated'
        : callEvent === 'rejected'
          ? 'Call rejected'
          : callEvent === 'cancelled'
            ? 'Call cancelled'
            : durationSeconds && durationSeconds > 0
              ? `Call ended (${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s)`
              : 'Call ended';

    socket.emit('send-message', {
      roomId: groupInfo._id,
      message: eventMessage,
      type: 'call_event',
      metadata: {
        callEvent,
        ...(durationSeconds != null ? { durationSeconds } : {}),
      },
    });
  };

  useEffect(() => {
    if (!socket) return;

    const onIncoming = (payload: {
      callerUserId: string;
      channelName: string | null;
      callType?: CallMode;
    }) => {
      if (!payload?.callerUserId) return;
      if (
        !belongsToCurrentDirectChat(payload.callerUserId) &&
        !belongsToCurrentGroupChat(payload.channelName)
      ) {
        return;
      }
      if (payload.callType) {
        setCallMode(payload.callType);
      }
      setIncomingCall({
        callerUserId: payload.callerUserId,
        channelName: payload.channelName,
        callType: payload.callType,
      });
      setActivePeerUserId(payload.callerUserId);
      setActiveChannelName(payload.channelName || '');
      setCallStatusText(
        payload.callType === 'video' ? 'Incoming video call...' : 'Incoming audio call...'
      );
    };

    const onAccepted = (payload: { calleeUserId: string; channelName: string | null }) => {
      if (
        !belongsToCurrentDirectChat(payload?.calleeUserId) &&
        !belongsToCurrentGroupChat(payload?.channelName)
      ) {
        return;
      }
      setIsDialing(false);
      setActivePeerUserId(payload.calleeUserId || targetUserId);
      setActiveChannelName(payload.channelName || activeChannelName || '');
      setCallStatusText('');
      setCallOpen(true);
    };

    const onRejected = (payload: { calleeUserId: string }) => {
      if (!belongsToCurrentDirectChat(payload?.calleeUserId) && !activeChannelName) return;
      setCallOpen(false);
      setIncomingCall(null);
      setIsDialing(false);
      setCallStatusText('Call rejected');
      setActiveChannelName('');
      setActivePeerUserId('');
      setGroupCallStartedAt(null);
      setPreparedCall(null);
    };

    const onCancelled = (payload: { callerUserId: string }) => {
      if (
        !belongsToCurrentDirectChat(payload?.callerUserId) &&
        !belongsToCurrentGroupChat(activeChannelName)
      ) {
        return;
      }
      setIncomingCall(null);
      setCallStatusText('Call cancelled');
    };

    const onEnded = (payload: { fromUserId: string; channelName: string | null }) => {
      if (
        !belongsToCurrentDirectChat(payload?.fromUserId) &&
        !belongsToCurrentGroupChat(payload?.channelName || activeChannelName)
      ) {
        return;
      }
      setCallOpen(false);
      setIncomingCall(null);
      setIsDialing(false);
      setCallStatusText('Call ended');
      setActiveChannelName('');
      setActivePeerUserId('');
      setGroupCallStartedAt(null);
      setPreparedCall(null);
    };

    const onNoAnswer = (payload: {
      callerUserId?: string;
      calleeUserId?: string;
      channelName?: string | null;
    }) => {
      // For group calls, one member not answering must not close the whole call.
      if (belongsToCurrentGroupChat(payload?.channelName)) return;
      if (
        !belongsToCurrentDirectChat(payload?.callerUserId) &&
        !belongsToCurrentDirectChat(payload?.calleeUserId)
      ) {
        return;
      }
      setIncomingCall(null);
      setCallOpen(false);
      setIsDialing(false);
      setActiveChannelName('');
      setPreparedCall(null);
    };

    socket.on('call:incoming', onIncoming);
    socket.on('call:accepted', onAccepted);
    socket.on('call:rejected', onRejected);
    socket.on('call:cancelled', onCancelled);
    socket.on('call:ended', onEnded);
    socket.on('call:no-answer', onNoAnswer);

    return () => {
      socket.off('call:incoming', onIncoming);
      socket.off('call:accepted', onAccepted);
      socket.off('call:rejected', onRejected);
      socket.off('call:cancelled', onCancelled);
      socket.off('call:ended', onEnded);
      socket.off('call:no-answer', onNoAnswer);
    };
  }, [
    socket,
    targetUserId,
    activeChannelName,
    isDirectChat,
    currentUserId,
    groupChannelPrefix,
    participantUserIds,
    belongsToCurrentDirectChat,
    belongsToCurrentGroupChat,
  ]);

  const isImageUrl = (message: string) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];
    return imageExtensions.some((ext) => message.toLowerCase().endsWith(ext));
  };

  const handleInputChange = (event: any) => {
    setInputValue(event.target.value);
  };

  const startCall = (mode: CallMode) => {
    if (!socket) return;
    setCallMode(mode);
    if (isDirectChat && targetUserId) {
      const channelName = buildChannelName();
      const agoraUid = buildAgoraUid();
      setIsDialing(true);
      setCallStatusText('Calling...');
      setActivePeerUserId(targetUserId);
      setActiveChannelName(channelName);
      prepareAgoraCall(channelName, agoraUid)
        .then((agoraCall) => {
          if (debugAgora) {
            // eslint-disable-next-line no-console
            console.log('[AgoraCall] emit call:initiate direct', {
              calleeUserId: targetUserId,
              channelName: agoraCall.channelName,
              uid: agoraCall.uid,
            });
          }
          setPreparedCall(agoraCall);
          socket.emit('call:initiate', {
            calleeUserId: targetUserId,
            channelName: agoraCall.channelName,
            callType: mode,
          });
          setCallOpen(true);
        })
        .catch((error: any) => {
          setIsDialing(false);
          setCallStatusText(error?.message || 'Unable to start call');
        });
      return;
    }

    // Group call: signal all participants + join common room channel.
    if (participantUserIds.length === 0) return;
    const nextGroupChannelName = `${groupChannelPrefix || 'group_'}${Date.now()}`.slice(0, 64);
    const agoraUid = buildAgoraUid();
    prepareAgoraCall(nextGroupChannelName, agoraUid)
      .then((agoraCall) => {
        if (debugAgora) {
          // eslint-disable-next-line no-console
          console.log('[AgoraCall] emit call:initiate group', {
            participantUserIds,
            channelName: agoraCall.channelName,
            uid: agoraCall.uid,
          });
        }
        participantUserIds.forEach((calleeId) => {
          socket.emit('call:initiate', {
            calleeUserId: calleeId,
            channelName: agoraCall.channelName,
            callType: mode,
          });
        });
        setPreparedCall(agoraCall);
        setIsDialing(false);
        setCallStatusText('');
        setActivePeerUserId('');
        setActiveChannelName(agoraCall.channelName);
        setGroupCallStartedAt(Date.now());
        sendGroupCallEvent('initiated');
        setCallOpen(true);
      })
      .catch((error: any) => {
        setCallStatusText(error?.message || 'Unable to start call');
      });
  };

  const acceptIncomingCall = () => {
    if (!socket || !incomingCall) return;
    if (incomingCall.callType) {
      setCallMode(incomingCall.callType);
    }
    const nextChannelName = incomingCall.channelName || '';
    const agoraUid = buildAgoraUid();
    prepareAgoraCall(nextChannelName, agoraUid)
      .then((agoraCall) => {
        if (debugAgora) {
          // eslint-disable-next-line no-console
          console.log('[AgoraCall] emit call:accept', {
            callerUserId: incomingCall.callerUserId,
            channelName: agoraCall.channelName,
            uid: agoraCall.uid,
          });
        }
        socket.emit('call:accept', {
          callerUserId: incomingCall.callerUserId,
          channelName: agoraCall.channelName || undefined,
        });
        setPreparedCall(agoraCall);
        setActivePeerUserId(incomingCall.callerUserId);
        setActiveChannelName(agoraCall.channelName);
        setIncomingCall(null);
        setCallStatusText('');
        if (!isDirectChat) {
          setGroupCallStartedAt(Date.now());
        }
        setCallOpen(true);
      })
      .catch((error: any) => {
        setCallStatusText(error?.message || 'Unable to accept call');
      });
  };

  const rejectIncomingCall = () => {
    if (!socket || !incomingCall) return;
    socket.emit('call:reject', {
      callerUserId: incomingCall.callerUserId,
      channelName: incomingCall.channelName ?? undefined,
    });
    setIncomingCall(null);
    setCallStatusText('');
  };

  const cancelOutgoingCall = () => {
    if (!socket) return;
    if (isDirectChat) {
      if (!activePeerUserId) return;
      socket.emit('call:cancel', {
        calleeUserId: activePeerUserId,
      });
    } else {
      participantUserIds.forEach((calleeId) => {
        socket.emit('call:cancel', {
          calleeUserId: calleeId,
        });
      });
    }
    setIsDialing(false);
    setCallStatusText('Call cancelled');
    setPreparedCall(null);
  };

  const endConnectedCall = () => {
    if (!socket) return;
    if (isDirectChat && activePeerUserId) {
      socket.emit('call:end', {
        otherUserId: activePeerUserId,
        channelName: activeChannelName || undefined,
      });
      setPreparedCall(null);
      return;
    }

    participantUserIds.forEach((otherUserId) => {
      socket.emit('call:end', {
        otherUserId,
        channelName: activeChannelName || undefined,
      });
    });

    setGroupCallStartedAt(null);
    setPreparedCall(null);
  };

  // const handleImageUpload = async (event: any) => {
  //   const imageFile = event.target.files[0];
  //   if (!imageFile) return;

  //   // Revoke the previous URL object if it exists
  //   if (selectedImage) {
  //     URL.revokeObjectURL(selectedImage);
  //   }

  //   const imageURL = URL.createObjectURL(imageFile);
  //   setSelectedImage(imageURL); // Store URL for preview
  //   setUploadedImageFile(imageFile); // Store the actual file for uploading

  //   // FormData for file upload
  //   const formData = new FormData();
  //   formData.append("image", imageFile);

  //   const response = await uploadImage(formData);
  //   const data = await response.data;

  //   if (data) {
  //     setInputValue(data.data.location);
  //     setFileName(data.data.originalname);
  //   }
  // };

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      submitMessage();
    }
  };

  const submitMessage = () => {
    sendMessageWithAck(inputValue);
    setInputValue("");
    setSelectedImage(null);
    if (inputRef) {
      inputRef.current.style.height = "56px";
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clears file input value
    }

    // Revoke the ObjectURL if an image was selected
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const formatDuration = (durationSeconds?: number) => {
    if (durationSeconds == null) return '';
    const mins = Math.floor(durationSeconds / 60);
    const secs = durationSeconds % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  const formatMessageTime = (createdAt: string): string => {
    if (!createdAt) return '';
    const date = new Date(createdAt);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const isCallEventMessage = (chat: chatProps) => {
    const normalizedMessage = (chat?.message || '').trim().toLowerCase();
    return (
      chat?.type === 'call_event' ||
      !!chat?.metadata?.callEvent ||
      normalizedMessage.startsWith('call initiated') ||
      normalizedMessage.startsWith('call started') ||
      normalizedMessage.startsWith('call rejected') ||
      normalizedMessage.startsWith('call cancelled') ||
      normalizedMessage.startsWith('call missed') ||
      normalizedMessage.startsWith('call ended')
    );
  };

  return (
    <Box sx={{px: { xs: 2, sm: 10 },
    py: 4,}} className="group-chat-container">
  <Box className="group-chat-header">
    <Box onClick={goBack} className="back-icon">
      <img className="back-arrow" src={LeftArrow} alt="back icon" />
    </Box>
    <Box className="group-details">
      <Box onClick={onClickDetails} className="group-avatars">
        {groupInfo?.profileIds
          ?.slice(0, 4)
          ?.map((profile: profileType, key) => {
            const avatarKey = `header-${profile._id || profile.userId?._id || key}`;
            const isBroken = shouldShowFallbackAvatar(avatarKey, profile.profilePic);
            return isBroken ? (
              <Box
                key={key}
                className={`group-avatar avatar-fallback ${key > 0 ? "avatar-offset" : ""}`}
              >
                <AccountCircleIcon className="avatar-fallback-icon" />
              </Box>
            ) : (
              <img
                key={key}
                className={`group-avatar ${key > 0 ? "avatar-offset" : ""}`}
                src={profile.profilePic}
                alt={profile.firstName}
                onError={() =>
                  setAvatarErrors((prev) => ({ ...prev, [avatarKey]: true }))
                }
              />
            );
          })}
      </Box>
      <p className="group-name">{groupInfo?.groupName}</p>
    </Box>
    <Box className="call-actions">
      <IconButton
        onClick={() => startCall('audio')}
        className="call-action-btn"
        aria-label="Start audio call"
      >
        <CallIcon />
      </IconButton>
      <IconButton
        onClick={() => startCall('video')}
        className="call-action-btn"
        aria-label="Start video call"
      >
        <VideocamIcon />
      </IconButton>
      {isDialing && (
        <IconButton
          onClick={cancelOutgoingCall}
          className="call-action-btn call-cancel-btn"
          aria-label="Cancel outgoing call"
        >
          <CallIcon />
        </IconButton>
      )}
    </Box>
  </Box>
  <Box ref={listRef} className="chat-history">
    {chatHistory.map((group, index) => (
      <Box key={index}>
        <h3 className="chat-date">{group?.date}</h3>
        <Box>
          {group?.messages?.map((chat: chatProps, index: number) =>
            isCallEventMessage(chat) ? (
              <Box key={index} className="call-event-row">
                <Box className="call-event-inline">
                  <Typography className="call-event-text">{chat?.message}</Typography>
                  <Typography className="call-event-time">
                    {formatMessageTime(chat.createdAt)}
                  </Typography>
                </Box>
              </Box>
            ) : currentUserId === chat?.senderId ? (
              <Box key={index} className="message-container sender">
                <Box className="message-bubble sender-bubble">
                  {isImageUrl(chat?.message) ? (
                    <img
                      src={chat?.message}
                      alt="uploaded file"
                      className="chat-image"
                    />
                  ) : (
                    <p className="message-text">{chat?.message}</p>
                  )}
                  <Typography className="message-time sender-time">
                    {formatMessageTime(chat.createdAt)}
                  </Typography>
                </Box>
                {shouldShowFallbackAvatar(
                  `chat-${chat?._id}-sender`,
                  chat?.senderProfilePic
                ) ? (
                  <Box className="user-avatar avatar-fallback">
                    <AccountCircleIcon className="avatar-fallback-icon" />
                  </Box>
                ) : (
                  <img
                    src={chat?.senderProfilePic}
                    alt="User Avatar"
                    className="user-avatar"
                    onError={() =>
                      setAvatarErrors((prev) => ({
                        ...prev,
                        [`chat-${chat?._id}-sender`]: true,
                      }))
                    }
                  />
                )}
              </Box>
            ) : (
              <Box key={index} className="message-container receiver">
                {shouldShowFallbackAvatar(
                  `chat-${chat?._id}-receiver`,
                  chat?.senderProfilePic
                ) ? (
                  <Box className="user-avatar avatar-fallback">
                    <AccountCircleIcon className="avatar-fallback-icon" />
                  </Box>
                ) : (
                  <img
                    src={chat?.senderProfilePic}
                    alt="User Avatar"
                    className="user-avatar"
                    onError={() =>
                      setAvatarErrors((prev) => ({
                        ...prev,
                        [`chat-${chat?._id}-receiver`]: true,
                      }))
                    }
                  />
                )}
                <Box className="message-bubble receiver-bubble">
                  <p className="message-text">{chat?.message}</p>
                  <Typography className="message-time receiver-time">
                    {formatMessageTime(chat.createdAt)}
                  </Typography>
                </Box>
              </Box>
            )
          )}
        </Box>
      </Box>
    ))}
  </Box>

  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#1c1c1c',
      borderRadius: '34px',
      width: '100%',
      background: `linear-gradient(rgb(23, 24, 30), rgb(29, 31, 41)) padding-box,
                    linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box`,
      border: "1.4px solid transparent",
    }}
  >
    {selectedImage && (
      <Box sx={{ marginRight: '10px', position: 'relative' }}>
        <img
          src={selectedImage}
          alt="file preview"
          style={{ width: "50px", height: "50px", borderRadius: '10px' }}
        />
        <IconButton
          onClick={() => {
            if (selectedImage) {
              URL.revokeObjectURL(selectedImage);
            }
            setSelectedImage(null);
            setInputValue("");
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            color: '#fff',
          }}
        >
          X
        </IconButton>
      </Box>
    )}

    <TextField
      variant="outlined"
      placeholder="Type message here..."
      value={selectedImage ? "" : inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      inputRef={inputRef}
      multiline
      maxRows={1}
      sx={{
        flex: 1,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'transparent',
          borderRadius: '34px',
          color: '#fff',
          '& fieldset': {
            border: 'none',
          },
          '& textarea': {
            padding: '4px', // This removes all padding from the textarea
            color: '#fff',
            margin: '0', // Make sure there is no margin as well
          },
        },
      }}
    />

    <IconButton
      sx={{ color: '#FF5722' }}
      onClick={submitMessage}
    >
      <img src={SendMessageIcon} alt="Send message" />
    </IconButton>
  </Box>

  <AgoraCallModal
    open={callOpen}
    onClose={() => {
      setCallOpen(false);
      setPreparedCall(null);
    }}
    onEndCall={endConnectedCall}
    channelName={preparedCall?.channelName || activeChannelName}
    uid={preparedCall?.uid || 0}
    mode={callMode}
    appId={preparedCall?.appId || appId}
    token={preparedCall?.token || null}
    tokenUid={preparedCall?.uid || null}
    calleeName={calleeName}
    calleeProfilePic={calleeProfilePic}
  />

  <Dialog open={!!incomingCall} onClose={rejectIncomingCall}>
    <DialogTitle>
      {incomingCall?.callType === 'video' ? 'Incoming Video Call' : 'Incoming Audio Call'}
    </DialogTitle>
    <DialogContent>
      <Typography>
        {incomingCall?.callType === 'video'
          ? 'Someone is video calling you.'
          : 'Someone is calling you.'}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={rejectIncomingCall} color="error" variant="outlined">
        Reject
      </Button>
      <Button onClick={acceptIncomingCall} variant="contained">
        Accept
      </Button>
    </DialogActions>
  </Dialog>
</Box>

  );
};
export default GroupChatDetails;
